import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { Send, CheckCircle2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const professions = [
  "Pair aidant / Pair-aidante",
  "Clinicien·ne",
  "Intervenant·e psychosocial·e",
  "Ergothérapeute",
  "Gestionnaire de cas",
  "Psychologue",
  "Travailleur·euse social·e",
  "Proche aidant·e",
  "Autre",
];

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-cream px-4 py-3.5 text-sm outline-none transition-colors focus:border-teal focus:ring-2 focus:ring-teal/20";

export default function Inscription() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    profession: "",
    organization: "",
    promo_code: "QC2026",
  });
  const [status, setStatus] = useState("idle");
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/inscriptions/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/inscriptions`, form);
      const link = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
      if (link) {
        setStatus("redirecting");
        const params = new URLSearchParams({
          prefilled_email: form.email,
          client_reference_id: res.data.inscription.id,
        });
        if (form.promo_code?.trim()) {
          params.set("prefilled_promo_code", form.promo_code.trim().toUpperCase());
        }
        window.location.href = `${link}?${params.toString()}`;
        return;
      }
      setStatus("done");
      confetti({
        particleCount: 160,
        spread: 75,
        origin: { y: 0.7 },
        colors: ["#00A896", "#FFC43D", "#EF476F", "#A8DADC"],
      });
    } catch (err) {
      setStatus("idle");
      const msg =
        err.response?.data?.detail ||
        "Une erreur est survenue. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur de validation.");
    }
  };

  return (
    <section id="inscription" className="bg-ink py-24 text-cream sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-sun">
            Inscription & paiement
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Réservez votre place{" "}
            <span className="text-outline-cream">dès maintenant</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70 sm:text-lg">
            Remplissez le formulaire : vous serez ensuite redirigé vers la page de
            paiement sécurisée Stripe pour régler votre inscription. Le code promo{" "}
            <strong className="text-sun">QC2026</strong> est déjà appliqué.
          </p>
          {count !== null && count < 20 && (
            <p
              data-testid="cpmc-inscription-count"
              className="mt-8 inline-block rounded-full border border-sun/40 bg-sun/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-sun"
            >
              Places limitées
            </p>
          )}
          <ul className="mt-10 space-y-3 text-sm text-cream/60">
            <li>— Début : 1er octobre 2026</li>
            <li>— 19 semaines · 3 h par semaine · 100 % en ligne</li>
            <li>— Certification CPMC reconnue à l'international</li>
            <li>
              — Coût : <span className="text-cream/40 line-through">2 250 $ CA</span>{" "}
              <strong className="text-sun">1 125 $ CA + taxes</strong> avec le code QC2026 (-50 % première cohorte)
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-3xl border border-cream/10 bg-cream/[0.05] p-8 backdrop-blur sm:p-10"
        >
          {count !== null && count >= 20 ? (
            <div data-testid="cpmc-inscription-full" className="flex h-full flex-col items-center justify-center py-16 text-center">
              <Users className="mb-6 h-16 w-16 text-coral" />
              <h3 className="font-display text-2xl font-bold sm:text-3xl">
                Cohorte complète
              </h3>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/70 sm:text-base">
                Les 20 places de la première cohorte sont toutes réservées.{" "}
                <a href="mailto:info@cpmc-quebec.ca" className="text-sun underline underline-offset-4 hover:text-teal">
                  Écrivez-nous
                </a>{" "}
                pour être informé·e de la prochaine cohorte.
              </p>
            </div>
          ) : status === "done" ? (
            <div data-testid="cpmc-inscription-success" className="flex h-full flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="mb-6 h-16 w-16 text-teal" />
              <h3 className="font-display text-2xl font-bold sm:text-3xl">
                Inscription reçue !
              </h3>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/70 sm:text-base">
                Merci {form.name.split(" ")[0]} ! Notre équipe vous contactera au{" "}
                <strong className="text-cream">{form.email}</strong> avec le lien de
                paiement sécurisé et les détails de la cohorte.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="cpmc-registration-form" className="space-y-5">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                  Prénom & nom *
                </label>
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  data-testid="cpmc-input-full-name"
                  className={inputCls}
                  placeholder="Ex. : Marie Tremblay"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                  Courriel *
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="cpmc-input-email"
                  className={inputCls}
                  placeholder="vous@exemple.ca"
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                    Profession / rôle *
                  </label>
                  <select
                    required
                    value={form.profession}
                    onChange={set("profession")}
                    data-testid="cpmc-select-profession"
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Choisir…
                    </option>
                    {professions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                    Organisation
                  </label>
                  <input
                    value={form.organization}
                    onChange={set("organization")}
                    data-testid="cpmc-input-organization"
                    className={inputCls}
                    placeholder="CIUSSS, clinique, OSBL…"
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                  Code promo
                </label>
                <input
                  value={form.promo_code}
                  onChange={set("promo_code")}
                  data-testid="cpmc-input-promo-code"
                  className={`${inputCls} font-mono uppercase`}
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="cpmc-btn-submit-registration"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-sun px-7 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-teal hover:text-cream disabled:opacity-60"
              >
                {status === "loading" || status === "redirecting" ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {status === "redirecting" ? "Redirection vers le paiement sécurisé…" : "Envoi en cours…"}
                  </>
                ) : (
                  <>
                    Réserver ma place — cohorte d'octobre
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                2 250 $ CAD — 50 % de rabais première cohorte avec le code QC2026
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
